import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  Autocomplete,
  AutocompleteProps,
  Option,
  Typography,
} from '@beeline/design-system-react/extracted-styles';

import { Building } from 'src/api/types';
import { getBuildingsState } from 'src/store/buildings/selectors';
import { fetchBuildings } from 'src/store/buildings/services';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { truphy } from 'src/utils/truphy';

import { TooltipWrapper } from '../TooltipWrapper/TooltipWrapper';

export type BuildingSelectItem = Option<Building>;

export const toBuildingSelectItem = (
  building: Building | undefined,
): BuildingSelectItem | undefined =>
  building && {
    id: building.buildingId,
    value: building,
  };

export const toBuilding = (
  item: BuildingSelectItem | undefined,
): Building | undefined => item?.value;

export interface BuildingSelectProps
  extends Omit<
    AutocompleteProps<Building>,
    | 'label'
    | 'size'
    | 'fullWidth'
    | 'placeholder'
    | 'noOptionsText'
    | 'options'
    | 'loading'
    | 'renderValue'
    | 'multiple'
    | 'values'
    | 'onChange'
    | 'value'
    | 'type'
    | 'onInputChange'
    | 'onInputClear'
  > {
  townId?: number;
  building: BuildingSelectItem | undefined;
  onChange: (value: BuildingSelectItem | undefined) => void;
  filterOptions?: (option: BuildingSelectItem) => boolean;
}

const renderValue = (buildings: BuildingSelectItem | null): string =>
  buildings?.value.address ?? '';

const defaultMakeOption = (option: BuildingSelectItem | null): JSX.Element => {
  return option ? (
    <TooltipWrapper title={option.value.address}>
      <Typography variant='body2' ellipsis>
        {option.value.address}
      </Typography>
    </TooltipWrapper>
  ) : (
    <></>
  );
};

const emptyBuildings: BuildingSelectItem[] = [];

export const BuildingSelect: FC<BuildingSelectProps> = ({
  townId,
  building,
  onChange,
  makeOption = defaultMakeOption,
  filterOptions = truphy,
  ...rest
}) => {
  const [input, setInput] = useState('');

  const dispatch = useAppDispatch();
  const { buildingsByTowns, buildingsById, loading } =
    useSelector(getBuildingsState);

  const options = useMemo((): BuildingSelectItem[] => {
    if (!townId) return emptyBuildings;

    const ids = buildingsByTowns.get(townId);
    if (!ids) return emptyBuildings;

    const result: BuildingSelectItem[] = [];
    for (const id of ids) {
      const item = toBuildingSelectItem(buildingsById.get(id));
      if (item && filterOptions(item)) result.push(item);
    }
    return result;
  }, [buildingsById, buildingsByTowns, filterOptions, townId]);

  const filteredOptions = useMemo((): BuildingSelectItem[] => {
    const search = input.toLowerCase();

    return options.filter(option =>
      option.value.address.toLowerCase().includes(search),
    );
  }, [input, options]);

  useEffect(() => {
    if (!townId) return;

    dispatch(fetchBuildings({ cached: true, townId }));
  }, [dispatch, townId]);

  const handleChange = useCallback(
    (nextValue: BuildingSelectItem | undefined) => {
      onChange(nextValue);
    },
    [onChange],
  );

  const handleClear = useCallback(async () => {
    setInput('');
    handleChange(undefined);
  }, [handleChange]);

  return (
    <Autocomplete
      {...(rest as AutocompleteProps<BuildingSelectItem>)}
      size='medium'
      label='Офис'
      fullWidth
      placeholder='выберите офис'
      noOptionsText='нет офисов'
      options={filteredOptions}
      loading={loading}
      renderValue={renderValue}
      makeOption={makeOption}
      multiple={false}
      value={building ?? null}
      onChange={handleChange}
      onInputChange={setInput}
      onInputClear={handleClear}
    />
  );
};
